import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

export const Booking = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    service: '',
    date: '',
    message: ''
  });

  const services = [
    "Arbeidsmiljøkartlegging",
    "Workshop / Fasilitering",
    "Lederstøtte",
    "Konfliktmekling",
    "Strategisk HR-rådgivning"
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }} 
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white p-12 rounded-[48px] shadow-3xl text-center space-y-8"
        >
          <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-3xl flex items-center justify-center mx-auto shadow-sm">
            <SafeIcon name="Check" className="text-4xl" />
          </div>
          <div className="space-y-4">
            <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">Takk for din henvendelse!</h2>
            <p className="text-slate-500 font-medium leading-relaxed">
              Vi har mottatt din forespørsel og tar kontakt innen kort tid for å bekrefte tidspunkt og neste steg.
            </p>
          </div>
          <Button variant="secondary" className="w-full py-4 text-sm font-black uppercase tracking-widest" onClick={() => setIsSubmitted(false)}>
            Tilbake til forsiden
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="py-24 bg-slate-50 min-h-screen">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          
          <div className="space-y-10">
            <div className="space-y-4">
              <h1 className="text-5xl font-black text-slate-900 uppercase tracking-tighter leading-none">
                Book en <br/> <span className="text-indigo-600">rådgivningssamtale</span>
              </h1>
              <p className="text-xl text-slate-500 font-medium">
                Velg tjenesten du trenger hjelp med, så finner vi en løsning som passer din virksomhet.
              </p>
            </div>

            <div className="space-y-6">
              {[
                { t: 'Gratis innledende prat', d: 'Vi kartlegger deres behov i løpet av 15-20 minutter.' },
                { t: 'Spesialiserte rådgivere', d: 'Snakk med eksperter innen organisasjonspsykologi.' },
                { t: 'Konkret tiltaksplan', d: 'Du går ut av møtet med en klar retning for veien videre.' }
              ].map((point, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <div className="w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-white shrink-0 mt-1">
                    <SafeIcon name="Check" size={12} />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900">{point.t}</h4>
                    <p className="text-sm text-slate-500 font-medium">{point.d}</p>
                  </div>
                </div>
              ))}
            </div>

            <Card className="bg-slate-900 border-none text-white overflow-hidden">
              <CardContent className="p-8 flex gap-6 items-center">
                <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center text-indigo-400 shrink-0">
                  <SafeIcon name="Phone" size={28} />
                </div>
                <div>
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Hastemøte?</p>
                  <p className="text-lg font-bold">Ring oss på +47 22 33 44 55</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="border-none shadow-3xl bg-white overflow-hidden">
            <CardHeader title="Forespørsel om rådgivning" subtitle="Fyll ut skjemaet under" />
            <CardContent className="p-10">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Navn</label>
                    <input 
                      required
                      type="text" 
                      className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none" 
                      placeholder="Ola Nordmann" 
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">E-post</label>
                    <input 
                      required
                      type="email" 
                      className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none" 
                      placeholder="ola@firma.no" 
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Bedrift</label>
                    <input 
                      required
                      type="text" 
                      className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none" 
                      placeholder="Bedrift AS" 
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Telefon</label>
                    <input 
                      type="tel" 
                      className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none" 
                      placeholder="+47 00 00 00 00" 
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Hva trenger dere hjelp med?</label>
                  <select 
                    required
                    className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm outline-none"
                    value={formData.service}
                    onChange={(e) => setFormData({...formData, service: e.target.value})}
                  >
                    <option value="">Velg tjeneste...</option>
                    {services.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Ønsket dato</label>
                  <input 
                    type="date" 
                    className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm" 
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Kort beskrivelse av utfordringen</label>
                  <textarea 
                    rows="3"
                    className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm" 
                    placeholder="Fortell oss litt om situasjonen..."
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                  />
                </div>

                <Button type="submit" className="w-full py-5 text-sm font-black uppercase tracking-widest shadow-xl shadow-indigo-100">
                  Send Forespørsel
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};